/**
 * On-screen controls for touch devices (docs/spec/04 §7): left and right bottom left, FIRE and NOVA
 * bottom right, pause top right. DOM, because several fingers at once are far simpler here than on
 * the canvas.
 */

import type { MenuAction } from '../ui/scene';
import type { GameKey, GameKeys } from './gameKeys';

export function isTouchDevice(): boolean {
  try {
    return window.matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window;
  } catch {
    return false;
  }
}

export class TouchControls {
  private readonly root: HTMLElement;
  private readonly nova: HTMLButtonElement;

  constructor(
    private readonly keys: GameKeys,
    private readonly onAction: (a: MenuAction) => void,
    private readonly onAny: () => void,
  ) {
    this.root = document.createElement('div');
    this.root.id = 'touch';
    this.root.hidden = true;
    this.nova = this.hold('nova', 'NOVA', 'bomb');
    const pause = this.button('pause', 'II');
    pause.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.onAny();
      this.onAction('pause');
    });
    this.root.append(this.hold('left', '◄', 'left'), this.hold('right', '►', 'right'), this.hold('fire', 'FIRE', 'fire'), this.nova, pause);
    document.body.append(this.root);
  }

  private button(cls: string, label: string): HTMLButtonElement {
    const b = document.createElement('button');
    b.className = cls;
    b.textContent = label;
    b.setAttribute('aria-label', cls);
    return b;
  }

  /** A button that holds a game key for as long as a finger is on it. */
  private hold(cls: string, label: string, key: GameKey): HTMLButtonElement {
    const b = this.button(cls, label);
    const source = `t:${cls}`;
    b.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.onAny();
      b.setPointerCapture(e.pointerId);
      this.keys.set(source, key, true);
      b.classList.add('on');
    });
    for (const t of ['pointerup', 'pointercancel', 'lostpointercapture'] as const) {
      b.addEventListener(t, () => {
        this.keys.set(source, key, false);
        b.classList.remove('on');
      });
    }
    return b;
  }

  /** Show or hide the controls; `nova` shows the Nova button (Supernova only). */
  setVisible(visible: boolean, nova = true): void {
    this.nova.hidden = !nova;
    if (this.root.hidden === !visible) return;
    this.root.hidden = !visible;
    if (!visible) for (const cls of ['left', 'right', 'fire', 'nova']) this.keys.release(`t:${cls}`);
  }

  get visible(): boolean {
    return !this.root.hidden;
  }
}
